import { ChevronLeft, ChevronRight, LogOut } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { useAuth } from '@/auth/auth-context';
import { useCompareMode } from '@/features/compare/use-compare-mode';
import { useSelectedFortnight } from '@/features/fortnights/use-selected-fortnight';
import { cn } from '@/lib/cn';

import { formatDay } from './format';

/**
 * The seven storyboard tabs, in storyboard order. This array IS the tab
 * dispatch — `NavLink` derives the active state from the router's matched
 * route. There is deliberately no `pathname.split('/')` switch anywhere in
 * this codebase (CONVENTIONS.md §4.4 names okr's 1,378-line version as the
 * anti-pattern to avoid).
 */
const TABS = [
  { to: '/pulse', label: 'Pulse' },
  { to: '/missions', label: 'Missions' },
  { to: '/plan', label: 'Plan' },
  { to: '/decisions', label: 'Decisions' },
  { to: '/blockers', label: 'Blockers' },
  { to: '/kpis', label: 'KPIs' },
  { to: '/drill', label: 'Drill' },
] as const;

/**
 * Fallback fortnight chrome (CONVENTIONS.md §5.1, decision D2).
 *
 * B1 reads the real calendar from `GET /api/v1/fortnights` and picks the row
 * the server marked `isCurrent`. These strings survive only for the seconds
 * before that resolves, and for the case where it never does — a bar with an
 * empty selector reads as a broken app, whereas the last-known fortnight with
 * an un-upgraded `WORKING COPY` chip understates rather than overstates.
 */
const FALLBACK_FORTNIGHT = { label: 'FN-16', asOf: '9 Aug 2026' } as const;

export type PublishState = 'working' | 'published';

export interface TopbarProps {
  /** Overrides the resolved fortnight. Present for tests and print mode. */
  fnLabel?: string;
  /** As-of date: the Sunday the fortnight closed, not the meeting date. */
  asOf?: string;
  publishState?: PublishState;
  onPreviousFortnight?: () => void;
  onNextFortnight?: () => void;
  className?: string;
}

function Chip({
  children,
  tone = 'default',
  className,
}: {
  children: React.ReactNode;
  tone?: 'default' | 'accent' | 'published';
  className?: string;
}) {
  return (
    <span
      className={cn(
        'whitespace-nowrap rounded px-2 py-1 text-xs font-bold tracking-wide',
        tone === 'default' && 'bg-chrome-elevated text-text-on-dark-muted',
        tone === 'accent' && 'bg-accent text-text-on-dark',
        tone === 'published' && 'bg-status-good-bg text-status-good',
        className
      )}
    >
      {children}
    </span>
  );
}

/**
 * The navy application bar: brand, seven-tab nav, fortnight selector, as-of
 * chip and publish-state chip — the storyboard's `.appbar`, at app scale.
 */
export function Topbar({
  fnLabel,
  asOf,
  publishState,
  onPreviousFortnight,
  onNextFortnight,
  className,
}: TopbarProps) {
  const { isEditor, user, signOut } = useAuth();
  const { fortnight, previous, next, isCurrent, select } = useSelectedFortnight();
  const compare = useCompareMode();

  // The API is the authority on all three: which fortnight is current, the
  // as-at date every figure in it is stated against, and whether the snapshot
  // has been published (and is therefore immutable, rule #1).
  const resolvedLabel = fnLabel ?? fortnight?.fnId ?? FALLBACK_FORTNIGHT.label;
  const resolvedAsOf = asOf ?? formatDay(fortnight?.asOfDate) ?? FALLBACK_FORTNIGHT.asOf;
  const resolvedPublishState: PublishState =
    publishState ?? (fortnight?.status === 'published' ? 'published' : 'working');

  // The props still win, so a print route or a test can pin the chrome. Only
  // when nothing is pinned do the arrows drive the URL — a control that says
  // it can move must actually move something.
  const goPrevious =
    onPreviousFortnight ?? (previous === null ? undefined : () => select(previous.fnId));
  const goNext = onNextFortnight ?? (next === null ? undefined : () => select(next.fnId));

  return (
    <header className={cn('bg-chrome text-text-on-dark', className)}>
      <div className="mx-auto flex max-w-board flex-wrap items-center gap-3.5 px-4 py-2.5">
        <NavLink
          to="/pulse"
          className="text-md font-extrabold tracking-wider text-text-on-dark"
          aria-label="JCORP AI Execution Dashboard — go to Pulse"
        >
          JCORP <span className="text-accent-on-dark">AI</span> · EXECUTION
        </NavLink>

        <nav aria-label="Dashboard pages" className="ml-2 flex flex-wrap gap-0.5">
          {TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={({ isActive }) =>
                cn(
                  'rounded px-2.5 py-1.5 text-xs font-semibold transition-colors',
                  isActive
                    ? 'bg-surface text-text-primary'
                    : 'text-text-on-dark-muted hover:bg-chrome-elevated hover:text-text-on-dark'
                )
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* Fortnight selector — ◂ FN-16 ▸. The label is the API's current
              fortnight; the arrows stay disabled until a fortnight lives in
              the URL, because a control that moves nothing is worse than a
              control that says it cannot. */}
          <div className="flex items-center gap-1 rounded bg-chrome-elevated px-1">
            <button
              type="button"
              onClick={goPrevious}
              disabled={goPrevious === undefined}
              aria-label="Previous fortnight"
              className="rounded p-1 text-text-on-dark-muted enabled:hover:text-text-on-dark disabled:opacity-40"
            >
              <ChevronLeft aria-hidden="true" className="size-3.5" />
            </button>
            <span className="px-1 text-xs font-bold tracking-wide text-text-on-dark">
              {resolvedLabel}
            </span>
            <button
              type="button"
              onClick={goNext}
              disabled={goNext === undefined}
              aria-label="Next fortnight"
              className="rounded p-1 text-text-on-dark-muted enabled:hover:text-text-on-dark disabled:opacity-40"
            >
              <ChevronRight aria-hidden="true" className="size-3.5" />
            </button>
          </div>

          <Chip>AS OF {resolvedAsOf.toUpperCase()}</Chip>

          {resolvedPublishState === 'published' ? (
            <Chip tone="published">✔ PUBLISHED</Chip>
          ) : (
            <Chip tone="accent">WORKING COPY</Chip>
          )}

          {/* Looking back has to be unmistakable. Every figure on screen is
              then a historical one, and a leadership audience reading last
              fortnight's numbers as this fortnight's is the single worst
              outcome of shipping history at all. Glyph + label, never colour
              alone (non-negotiable rule #4). */}
          {isCurrent || fortnight === null ? null : (
            <button
              type="button"
              onClick={() => select(null)}
              className="rounded bg-status-warn-bg px-2 py-1 text-xs font-bold tracking-wide text-status-warn hover:underline"
            >
              ▲ HISTORY · BACK TO CURRENT
            </button>
          )}

          {isEditor ? (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                cn(
                  'rounded px-2 py-1 text-xs font-bold tracking-wide',
                  isActive
                    ? 'bg-surface text-text-primary'
                    : 'bg-chrome-elevated text-text-on-dark-muted hover:text-text-on-dark'
                )
              }
            >
              ADMIN
            </NavLink>
          ) : null}

          {/* Compare mode. A pressed toggle rather than a link, because it is
              a lens over the page you are on and not a place to go — and
              `aria-pressed` is what tells a screen reader it is currently on,
              which colour alone does not. */}
          <button
            type="button"
            onClick={compare.toggle}
            aria-pressed={compare.isOn}
            title="Show the change since last fortnight"
            className={cn(
              'rounded px-2 py-1 text-xs font-bold tracking-wide',
              compare.isOn
                ? 'bg-accent text-text-on-dark'
                : 'bg-chrome-elevated text-text-on-dark-muted hover:text-text-on-dark'
            )}
          >
            {compare.isOn ? '✔ COMPARE' : 'COMPARE'}
          </button>

          <NavLink
            to={`/meeting${fortnight === null ? '' : `?fn=${encodeURIComponent(fortnight.fnId)}`}`}
            className="rounded bg-chrome-elevated px-2 py-1 text-xs font-bold tracking-wide text-text-on-dark-muted hover:text-text-on-dark"
          >
            MEETING
          </NavLink>

          {/* The pack. A plain link and a new tab rather than a download
              button: v1 is download-only (decision D5), and the browser's own
              print-to-PDF is what produces the file. The route is the same one
              a headless renderer would load, so the two cannot diverge. */}
          <NavLink
            to={`/print${fortnight === null ? '' : `?fn=${encodeURIComponent(fortnight.fnId)}`}`}
            target="_blank"
            rel="noreferrer"
            className="rounded bg-chrome-elevated px-2 py-1 text-xs font-bold tracking-wide text-text-on-dark-muted hover:text-text-on-dark"
          >
            PACK
          </NavLink>

          {/* Who is signed in, and the way out. The local part only — a
              topbar has no room for an address, and the full one is one
              hover away. Absent under the dev stub, where there is no
              session to end. */}
          {user === null ? null : (
            <div className="flex items-center gap-1.5">
              <span
                title={user.email}
                className="max-w-[10rem] truncate text-xs font-semibold text-text-on-dark-muted"
              >
                {user.email.split('@')[0]}
              </span>
              <button
                type="button"
                onClick={signOut}
                aria-label={`Sign out ${user.email}`}
                className="rounded bg-chrome-elevated p-1 text-text-on-dark-muted hover:text-text-on-dark"
              >
                <LogOut aria-hidden="true" className="size-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
